/**
 * 设置项 Schema（docs/插件开发.md 第 4 节）：JSON Schema 子集 + x- 界面标注，家长端按它生成设置表单。
 * 用在两处：游戏的 params_schema（新建互动游戏内容时全部字段），以及模块 payload Schema 里 x-assign 的字段（布置页的快捷设置）。
 * 与 iOS Parent/Common/SchemaForm.swift 的规则一致；纯函数，单元测试见 schema-form.test.ts。
 */
import type { JSONObject, JSONValue } from "../core/models";

export type FieldKind =
  | { kind: "integer"; min: number; max: number; options: number[]; unit: string | null }
  | { kind: "bool" }
  | { kind: "choice"; values: string[]; labels: Record<string, string> }
  | { kind: "text"; maxLength: number }
  /** [起始页, 结束页]，没有值表示全部；最大值取 hints.page_count */
  | { kind: "page_range" };

export interface FormField {
  key: string;
  title: string;
  description: string | null;
  kind: FieldKind;
  defaultValue: JSONValue | undefined;
  assign: boolean;
  /** 其它字段等于这些值时才显示 */
  visibleIf: JSONObject;
}

export type FormValues = Record<string, JSONValue>;

function obj(v: unknown): JSONObject | null {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as JSONObject) : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;
}

function key(v: JSONValue | undefined): string {
  if (v === undefined) return "undefined";
  if (Array.isArray(v)) return `[${v.map(key).join(",")}]`;
  const o = obj(v);
  if (o) return `{${Object.keys(o).sort().map((k) => `${JSON.stringify(k)}:${key(o[k])}`).join(",")}}`;
  return JSON.stringify(v);
}

export function sameJSON(a: JSONValue | undefined, b: JSONValue | undefined): boolean {
  return key(a) === key(b);
}

/** 解析 Schema 的顶层字段：先按 x-order，再按字段名；没有 title 或类型不支持的字段跳过 */
export function parseFormSchema(schema: JSONValue | null | undefined, opts: { assignOnly?: boolean } = {}): FormField[] {
  const s = obj(schema);
  const props = obj(s?.properties);
  if (!s || !props) return [];
  const order = Array.isArray(s["x-order"]) ? s["x-order"].filter((k): k is string => typeof k === "string" && k in props) : [];
  const keys = [...order, ...Object.keys(props).filter((k) => !order.includes(k)).sort()];
  const out: FormField[] = [];
  for (const k of keys) {
    const p = obj(props[k]);
    if (!p || typeof p.title !== "string") continue;
    const assign = p["x-assign"] === true;
    if (opts.assignOnly && !assign) continue;
    let kind: FieldKind;
    const values = Array.isArray(p.enum) ? p.enum.filter((x): x is string => typeof x === "string") : [];
    if (p["x-widget"] === "page_range") {
      kind = { kind: "page_range" };
    } else if (values.length) {
      const labels: Record<string, string> = {};
      for (const [v, l] of Object.entries(obj(p["x-enum-labels"]) ?? {})) if (typeof l === "string") labels[v] = l;
      kind = { kind: "choice", values, labels };
    } else if (p.type === "integer" || p.type === "number") {
      const options = Array.isArray(p["x-options"]) ? p["x-options"].map(num).filter((x): x is number => x != null) : [];
      kind = { kind: "integer", min: num(p.minimum) ?? 0, max: num(p.maximum) ?? 999, options, unit: typeof p["x-unit"] === "string" ? p["x-unit"] : null };
    } else if (p.type === "boolean") {
      kind = { kind: "bool" };
    } else if (p.type === "string") {
      kind = { kind: "text", maxLength: num(p.maxLength) ?? 100 };
    } else {
      continue;
    }
    out.push({
      key: k,
      title: p.title,
      description: typeof p.description === "string" ? p.description : null,
      kind,
      defaultValue: p.default,
      assign,
      visibleIf: obj(p["x-visible-if"]) ?? {},
    });
  }
  return out;
}

/** 当前值（没填用默认值） */
export function fieldValue(f: FormField, values: FormValues): JSONValue | undefined {
  return values[f.key] ?? f.defaultValue;
}

export function isFieldVisible(f: FormField, values: FormValues, fields: FormField[]): boolean {
  return Object.entries(f.visibleIf).every(([k, want]) => sameJSON(values[k] ?? fields.find((x) => x.key === k)?.defaultValue, want));
}

/**
 * 把表单的值合并进对象：被 x-visible-if 隐藏的字段删掉；和默认值相同、原来又没有的字段不写；page_range 为 null 时删掉。
 */
export function mergeFormValues(values: FormValues, original: JSONObject, fields: FormField[]): JSONObject {
  const out: JSONObject = { ...original };
  const all = { ...original, ...values };
  for (const f of fields) {
    if (!isFieldVisible(f, all, fields)) {
      delete out[f.key];
      continue;
    }
    const v = values[f.key];
    if (v === undefined) continue;
    if (v === null) {
      delete out[f.key];
      continue;
    }
    if (original[f.key] === undefined && sameJSON(v, f.defaultValue)) continue;
    out[f.key] = v;
  }
  return out;
}

/** 页码范围字段的值 [起, 止]，没有返回 null */
export function pageRangeOf(fields: FormField[], values: FormValues): [number, number] | null {
  const f = fields.find((x) => x.kind.kind === "page_range");
  const r = f ? values[f.key] : undefined;
  if (!Array.isArray(r) || r.length !== 2) return null;
  const [a, b] = r.map(num);
  return a != null && b != null ? [a, b] : null;
}

/** 改了页码范围时，复制出来的内容标题带上页码 */
export function pageRangeSuffix(fields: FormField[], values: FormValues): string {
  const r = pageRangeOf(fields, values);
  return r ? `（第 ${r[0]}–${r[1]} 页）` : "";
}
